import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Search, User, LogOut, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useState } from "react";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cursos?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 w-full z-50 transition-all duration-300 bg-gradient-to-b from-black/90 via-black/80 to-transparent backdrop-blur-sm border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="/logo-v4.png"
              srcSet="/logo-v4.png 1x, /logo-2x.png 2x, /logo-hd.png 4x"
              alt="CONTAFLIX"
              className="h-9 md:h-11 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/cursos"
              className="text-white/80 hover:text-white transition-colors font-medium text-sm"
              data-testid="nav-cursos"
            >
              Cursos
            </Link>
            {user && (
              <Link
                to="/mis-cursos"
                className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                data-testid="nav-mis-cursos"
              >
                Mis cursos
              </Link>
            )}
            <Link
              to="/plantillas"
              className="text-white/80 hover:text-white transition-colors font-medium text-sm"
              data-testid="nav-plantillas"
            >
              Plantillas
            </Link>
            {user && (
              <Link
                to="/mis-datos"
                className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                data-testid="nav-mis-datos"
              >
                Mis datos
              </Link>
            )}
            {user && user.role === "admin" && (
              <Link
                to="/admin"
                className="text-[#E50914] hover:text-[#E50914]/80 transition-colors font-medium text-sm"
                data-testid="nav-admin"
              >
                Panel Admin
              </Link>
            )}
            <Link
              to="/contaflix-pro"
              className="text-white/80 hover:text-white transition-colors font-medium text-sm"
              data-testid="nav-pro"
            >
              ContaFLIX PRO
            </Link>
          </nav>

          {/* Search and Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Buscar cursos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 bg-white/5 border-white/10 focus:border-[#E50914]/50 focus:ring-[#E50914]/20 text-white placeholder:text-white/30 h-10"
                data-testid="search-input"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            </form>

            {user ? (
              <div className="flex items-center space-x-3">
                <Link to="/mis-datos">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    data-testid="user-profile-btn"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/auth">
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="login-btn"
                  >
                    Iniciar sesion
                  </Button>
                </Link>
                <Link to="/contaflix-pro">
                  <Button
                    className="bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold"
                    data-testid="subscribe-btn"
                  >
                    Suscribirme
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-4 border-t border-white/10 pt-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Buscar cursos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-10"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            </form>
            <nav className="flex flex-col space-y-3">
              <Link
                to="/cursos"
                className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Cursos
              </Link>
              {user && (
                <Link
                  to="/mis-cursos"
                  className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mis cursos
                </Link>
              )}
              <Link
                to="/plantillas"
                className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Plantillas
              </Link>
              {user && (
                <Link
                  to="/mis-datos"
                  className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mis datos
                </Link>
              )}
              {user && user.role === "admin" && (
                <Link
                  to="/admin"
                  className="text-[#E50914] hover:text-[#E50914]/80 transition-colors font-medium text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Panel Admin
                </Link>
              )}
              <Link
                to="/contaflix-pro"
                className="text-white/80 hover:text-white transition-colors font-medium text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                ContaFLIX PRO
              </Link>
              {user ? (
                <Button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 w-full justify-start"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesion
                </Button>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10 w-full"
                    >
                      Iniciar sesion
                    </Button>
                  </Link>
                  <Link to="/contaflix-pro" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="bg-[#E50914] hover:bg-[#E50914]/90 text-white w-full">
                      Suscribirme
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
